import React, { FC, useState, useCallback, useMemo } from 'react';
import ReactDropdownSelect, { SelectMethods } from 'react-dropdown-select';
import { Select as AntSelect, Checkbox } from 'antd';
import { ChevronDownIcon } from 'components/Elements/CustomSVGIcon';
import type { DropdownOption } from './types';
import './dropdown.scss';

interface DropdownProps {
    options: DropdownOption[];
    onSelect: (values: DropdownOption[]) => void;
    values: DropdownOption[];
    multi?: boolean;
    placeholder?: string;
    disabled?: boolean;
    // Affects single-select only: turns the control into a text input that
    // filters the list as you type. Multi-select is always searchable (Ant's
    // own showSearch) and returns before this is read, so passing it with
    // `multi` is a no-op rather than a conflict.
    searchable?: boolean;
    // Single-select only: node pinned to the top of the open option list
    // (e.g. a filter control). Rendered above the options, not selectable.
    listHeader?: React.ReactNode;
    // eslint-disable-next-line react/require-default-props
    onClose?: () => void;
}

const customStyles = {
    minHeight: '50px',
    borderRadius: '4px',
    border: '0.5px solid #2B2D42',
};

const Dropdown: FC<DropdownProps> = ({
    options = [],
    onSelect,
    values = [],
    multi = false,
    placeholder = 'Select...',
    disabled = false,
    searchable = false,
    listHeader,
    onClose,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const onDropdownOpen = useCallback(() => {
        setDropdownOpen(true);
    }, []);

    const onDropdownClose = useCallback(() => {
        setDropdownOpen(false);
        onClose?.();
    }, [onClose]);

    const getItem = (item: DropdownOption, methods: SelectMethods<DropdownOption>) => (
        <div className="dropdown-item-wrapper">
            <span className="item-label" onClick={() => methods.addItem(item)}>{item.label}</span>
        </div>
    );

    const getContentRenderer = (
        state: { search: string },
        methods: { setSearch: (event: React.ChangeEvent<HTMLInputElement>) => void },
    ) => {
        if (!searchable) {
            return (
                <div className={`custom-content-renderer${!values[0]?.label ? ' placeholder' : ''}`}>
                    {values[0]?.label || placeholder}
                </div>
            );
        }

        // react-dropdown-select hides its own search box behind a custom content
        // renderer, so the input has to be driven from its state/methods here.
        return (
            <div className="custom-content-renderer searchable">
                <input
                    type="text"
                    className={`dropdown-search-input${!values[0]?.label ? ' placeholder' : ''}`}
                    value={state.search}
                    onChange={methods.setSearch}
                    placeholder={values[0]?.label || placeholder} />
            </div>
        );
    };

    const getDropdownHandleRenderer = useCallback(() => (
        <div className={`dropdown-handle ${dropdownOpen ? 'rotate' : ''}`}>
            <ChevronDownIcon w={20} />
        </div>
    ), [dropdownOpen]);

    const handleChange = useCallback((selectedValues: string[]) => {
        const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));
        onSelect(selectedOptions);
    }, [options, onSelect]);

    const selectedValuesSet = useMemo(() => new Set(values.map(v => v.value)), [values]);

    const renderOption = useCallback((option: { value?: string | number; label?: React.ReactNode }) => (
        <div className="dropdown-option">
            <Checkbox checked={selectedValuesSet.has(String(option.value))} />
            <span>{option.label}</span>
        </div>
    ), [selectedValuesSet]);

    const selectOptions = useMemo(
        () => options.map(opt => ({ value: opt.value, label: opt.label })),
        [options],
    );

    // Move focus to the option `delta` places away, keeping the roving tabindex
    // pattern's single tab stop intact.
    const moveOptionFocus = (event: React.KeyboardEvent<HTMLDivElement>, delta: number) => {
        const list = event.currentTarget.closest('.dropdown-option-list');
        if (!list) return;

        const optionEls = Array.from(list.querySelectorAll<HTMLElement>('[role="option"]'));
        const next = optionEls[optionEls.indexOf(event.currentTarget) + delta];
        if (next) {
            event.preventDefault();
            next.focus();
        }
    };

    // react-dropdown-select renders the option list itself; supplying a header
    // means taking that over, so re-create the item rows (and an empty state)
    // exactly as the default renderer would.
    const getDropdownRenderer = (
        methods: { searchResults: () => DropdownOption[]; addItem: (item: DropdownOption) => void },
    ) => {
        const results = methods.searchResults();
        // Roving tabindex: exactly one option is a tab stop — the selected one,
        // or the first when nothing is selected. Arrow keys move between them.
        const selectedIndex = results.findIndex((item) => selectedValuesSet.has(String(item.value)));
        const tabbableIndex = selectedIndex === -1 ? 0 : selectedIndex;

        return (
            <div className="dropdown-list-with-header">
                {/* Outside the listbox: it holds controls, not options. */}
                <div className="dropdown-list-header">{listHeader}</div>
                {results.length === 0 ? (
                    <div className="dropdown-no-results">No options match your search.</div>
                ) : (
                    // The listbox owns ONLY option elements, as ARIA requires.
                    <div className="dropdown-option-list" role="listbox">
                        {results.map((item, index) => (
                            <div
                                key={item.value}
                                className="dropdown-item-wrapper"
                                role="option"
                                aria-selected={selectedValuesSet.has(String(item.value))}
                                tabIndex={index === tabbableIndex ? 0 : -1}
                                onClick={() => methods.addItem(item)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        methods.addItem(item);
                                    } else if (event.key === 'ArrowDown') {
                                        moveOptionFocus(event, 1);
                                    } else if (event.key === 'ArrowUp') {
                                        moveOptionFocus(event, -1);
                                    }
                                }}>
                                {/* Presentational: keeps the default renderer's
                                    .item-label styling. */}
                                <span className="item-label">{item.label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Multi-select using Ant Design Select
    if (multi) {
        return (
            <div className="custom-dropdown-container multi-select">
                <AntSelect
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%', minHeight: '50px', fontSize: '18px' }}
                    placeholder={placeholder}
                    value={values.map(v => v.value)}
                    onChange={handleChange}
                    optionLabelProp="label"
                    suffixIcon={<ChevronDownIcon w={20} />}
                    menuItemSelectedIcon={null}
                    optionRender={renderOption}
                    maxTagCount={0}
                    maxTagPlaceholder={(omittedValues) => (omittedValues.length === 1 ? omittedValues[0]?.label : `${omittedValues.length} Selected`)}
                    options={selectOptions} />
            </div>
        );
    }

    // Single select using react-dropdown-select
    return (
        <div className={`custom-dropdown-container${disabled ? ' disabled' : ''}`}>
            <ReactDropdownSelect
                disabled={disabled}
                contentRenderer={({ state, methods }) => getContentRenderer(state, methods)}
                searchable={searchable}
                searchBy="label"
                clearOnSelect
                {...(listHeader ? { dropdownRenderer: ({ methods }) => getDropdownRenderer(methods) } : {})}
                style={customStyles}
                options={options}
                onChange={onSelect}
                onDropdownOpen={onDropdownOpen}
                onDropdownClose={onDropdownClose}
                values={values}
                color="#da0812"
                itemRenderer={({ item, methods }) => getItem(item, methods)}
                dropdownHandleRenderer={getDropdownHandleRenderer} />
        </div>
    );
};

Dropdown.defaultProps = {
    multi: false,
    placeholder: 'Select...',
    disabled: false,
    searchable: false,
    listHeader: null,
};

export default Dropdown;
